/**
 * TimelineWidget - Chronological view of tasks
 */

import React, { useState } from 'react';
import { Circle, CheckCircle2 } from 'lucide-react';
import { WidgetWrapper } from './WidgetWrapper';
import { AdaptiveModal } from '../AdaptiveModal';
import type { TimelineWidgetConfig } from '../../types/widgets';
import { useTodos } from '../../hooks/useTodos';
import { TaskStatus } from '../../types';

interface TimelineWidgetProps {
  config: TimelineWidgetConfig;
  widgetId: string;
  windowId: string;
}

export function TimelineWidget({ config, widgetId, windowId }: TimelineWidgetProps) {
  const [expanded, setExpanded] = useState(false);
  const { todos } = useTodos();

  // Filter and sort by date
  const daysToShow = config.daysToShow ?? 7;
  const today = new Date();
  const startDate = new Date(today);
  startDate.setDate(today.getDate() - Math.floor(daysToShow / 2));

  const timelineTodos = todos
    .filter((todo) => {
      const date = todo.deadline;
      if (!date) return false;
      if (!config.showCompleted && todo.status === TaskStatus.COMPLETED) return false;
      const todoDate = new Date(date);
      const endDate = new Date(startDate);
      endDate.setDate(startDate.getDate() + daysToShow);
      return todoDate >= startDate && todoDate <= endDate;
    })
    .sort((a, b) => {
      const dateA = a.deadline || '';
      const dateB = b.deadline || '';
      return dateA.localeCompare(dateB);
    });

  // Group by date
  const grouped = timelineTodos.reduce((acc, todo) => {
    const date = (todo.deadline || '').split('T')[0];
    if (!acc[date]) acc[date] = [];
    acc[date].push(todo);
    return acc;
  }, {} as Record<string, typeof timelineTodos>);

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const todayStr = today.toISOString().split('T')[0];
    if (dateStr === todayStr) return 'Today';
    return date.toLocaleDateString('default', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  const timelineContent = (
      <div className="p-2 h-full overflow-auto">
        {Object.keys(grouped).length === 0 ? (
          <div className="flex items-center justify-center h-20 text-gray-400 text-sm">
            No tasks in this period
          </div>
        ) : (
          <div className="relative pl-4">
            {/* Vertical line */}
            <div className="absolute left-1.5 top-0 bottom-0 w-0.5 bg-gray-200 dark:bg-gray-700" />

            {Object.entries(grouped).map(([date, dateTodos]) => (
              <div key={date} className="mb-4 relative">
                {/* Date marker */}
                <div className="absolute -left-4 w-3 h-3 rounded-full bg-blue-500 border-2 border-white dark:border-gray-800" />

                <div className="pl-4">
                  <h4 className="text-xs font-medium text-gray-500 mb-1">{formatDate(date)}</h4>
                  <ul className="space-y-1">
                    {dateTodos.map((todo) => (
                      <li
                        key={todo.id}
                        className="flex items-center gap-2 p-1.5 rounded hover:bg-gray-50 dark:hover:bg-gray-700"
                      >
                        {todo.status === TaskStatus.COMPLETED ? (
                          <CheckCircle2 className="w-3.5 h-3.5 text-green-500" />
                        ) : (
                          <Circle className="w-3.5 h-3.5 text-gray-400" />
                        )}
                        <span
                          className={`text-sm ${
                            todo.status === TaskStatus.COMPLETED
                              ? 'line-through text-gray-400'
                              : 'text-gray-800 dark:text-gray-200'
                          }`}
                        >
                          {todo.title}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
  );

  return (
    <>
      <WidgetWrapper title="Timeline" widgetId={widgetId} windowId={windowId} onHeaderClick={() => setExpanded(true)}>
        {timelineContent}
      </WidgetWrapper>
      {expanded && (
        <AdaptiveModal isOpen onClose={() => setExpanded(false)} title="Timeline" size="xl">
          <div className="max-h-[80vh] overflow-auto">{timelineContent}</div>
        </AdaptiveModal>
      )}
    </>
  );
}
